/**
 * ALOHANOVA UNIVERSE - Hawaiian Language Learning (Olelo Hawaii)
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 * 
 * First Hawaiian AI Education Platform
 */

import React, { useState } from 'react';
import { Book, Volume2, Award, Star, Globe } from 'lucide-react';
import Enhanced8KGalaxyBackground from '../components/Enhanced8KGalaxyBackground';

const HawaiianLearning = () => {
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [userInput, setUserInput] = useState('');
  const [feedback, setFeedback] = useState('');

  const lessons = [
    {
      id: 1,
      title: 'ʻŌlelo Hawaiʻi Basics',
      level: 'Beginner',
      words: [
        { hawaiian: 'ʻAloha', english: 'Hello/Love/Compassion', pronunciation: 'ah-LOH-ha' },
        { hawaiian: 'Mahalo', english: 'Thank you', pronunciation: 'mah-HAH-loh' },
        { hawaiian: 'ʻE komo mai', english: 'Welcome', pronunciation: 'eh KOH-moh my' },
        { hawaiian: 'ʻA hui hou', english: 'Until we meet again', pronunciation: 'ah HOO-ee ho' },
        { hawaiian: 'Pehea ʻoe', english: 'How are you', pronunciation: 'peh-HEH-ah oh-eh' }
      ]
    },
    {
      id: 2,
      title: 'Hawaiian Culture & Traditions',
      level: 'Intermediate',
      words: [
        { hawaiian: 'Kuleana', english: 'Responsibility/Privilege', pronunciation: 'koo-leh-AH-nah' },
        { hawaiian: 'Mālama', english: 'To care for/Protect', pronunciation: 'MAH-lah-mah' },
        { hawaiian: 'Pono', english: 'Righteousness/Balance', pronunciation: 'POH-noh' },
        { hawaiian: 'Moʻokūʻauhau', english: 'Genealogy/Heritage', pronunciation: 'moh-oh-KOO-ow-how' },
        { hawaiian: 'Kapu', english: 'Sacred/Forbidden', pronunciation: 'KAH-poo' }
      ]
    },
    {
      id: 3,
      title: 'Numbers & Counting',
      level: 'Beginner',
      words: [
        { hawaiian: 'ʻEkahi', english: 'One', pronunciation: 'eh-KAH-hee' },
        { hawaiian: 'ʻElua', english: 'Two', pronunciation: 'eh-LOO-ah' },
        { hawaiian: 'ʻEkolu', english: 'Three', pronunciation: 'eh-KOH-loo' },
        { hawaiian: 'ʻEhā', english: 'Four', pronunciation: 'eh-HAH' },
        { hawaiian: 'ʻElima', english: 'Five', pronunciation: 'eh-LEE-mah' }
      ]
    }
  ];

  const speakWord = (word) => {
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(word.hawaiian);
      utterance.lang = 'haw'; // Hawaiian language code
      utterance.rate = 0.8; // Slower for learning
      speechSynthesis.speak(utterance);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <Enhanced8KGalaxyBackground quality="medium" />
      
      <div className="relative z-10 container mx-auto px-6 py-12 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-6xl font-bold text-white mb-4">🌺 ʻŌlelo Hawaiʻi</h1>
          <p className="text-2xl text-yellow-400 mb-2">Hawaiian Language Learning</p>
          <p className="text-gray-300">The first AI-powered Hawaiian education platform</p>
        </div>

        {/* Lessons Grid */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {lessons.map(lesson => (
            <div
              key={lesson.id}
              onClick={() => setSelectedLesson(lesson)}
              className={`bg-gray-800/70 backdrop-blur-xl rounded-2xl p-6 border-2 cursor-pointer transition transform hover:scale-105 ${
                selectedLesson?.id === lesson.id
                  ? 'border-yellow-400 shadow-xl'
                  : 'border-purple-500/30 hover:border-purple-500/50'
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <Book className="w-8 h-8 text-yellow-400" />
                <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                  lesson.level === 'Beginner' ? 'bg-green-500/30 text-green-300' : 'bg-blue-500/30 text-blue-300'
                }`}>
                  {lesson.level}
                </span>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">{lesson.title}</h3>
              <p className="text-gray-400 text-sm">{lesson.words.length} words</p>
            </div>
          ))}
        </div>

        {/* Selected Lesson */}
        {selectedLesson && (
          <div className="bg-gray-800/70 backdrop-blur-xl rounded-2xl p-8 border border-yellow-500/30">
            <h2 className="text-3xl font-bold text-white mb-6">{selectedLesson.title}</h2>
            
            <div className="space-y-4">
              {selectedLesson.words.map((word, index) => (
                <div key={index} className="bg-gray-900/50 p-6 rounded-xl border border-gray-700 hover:border-yellow-500/50 transition">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-yellow-400 mb-1">{word.hawaiian}</h3>
                      <p className="text-gray-400 text-sm italic">{word.pronunciation}</p>
                    </div>
                    <button
                      onClick={() => speakWord(word)}
                      className="p-3 bg-blue-600 hover:bg-blue-700 rounded-full transition"
                      title="Pronounce"
                    >
                      <Volume2 className="w-5 h-5 text-white" />
                    </button>
                  </div>
                  <p className="text-gray-200 text-lg">{word.english}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Features */}
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          <div className="bg-blue-900/30 backdrop-blur-md p-6 rounded-xl border border-blue-500/30 text-center">
            <Globe className="w-12 h-12 text-blue-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">AI-Powered</h3>
            <p className="text-gray-300 text-sm">Learn with Queen Supernova AI tutor</p>
          </div>
          
          <div className="bg-purple-900/30 backdrop-blur-md p-6 rounded-xl border border-purple-500/30 text-center">
            <Volume2 className="w-12 h-12 text-purple-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Native Voice</h3>
            <p className="text-gray-300 text-sm">Authentic Hawaiian pronunciation</p>
          </div>
          
          <div className="bg-yellow-900/30 backdrop-blur-md p-6 rounded-xl border border-yellow-500/30 text-center">
            <Award className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Cultural Context</h3>
            <p className="text-gray-300 text-sm">Learn traditions and history</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HawaiianLearning;